import { Injectable, Inject, PLATFORM_ID, signal, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Plan } from '@sp-shared';
import { Stripe } from '@stripe/stripe-js';
import { environment } from '@sp-client/environments/environment';
import { ApiService } from './api.service';
import { PlanService } from './plan.service';
import { LogService } from './log.service';
import { RadioButtonOption } from '@sp-client/app/interfaces/radio-button-option.interface';
import { StripeService } from 'ngx-stripe';
import { firstValueFrom } from 'rxjs';

export interface Pricing {
	oneTime: number;
	subscription: number;
	currency: string;
}

export interface CheckoutSession {
	sessionId: string;
	url: string;
}

export interface PaymentVerification {
	success: boolean;
	plan: Plan;
}

@Injectable({
	providedIn: 'root'
})
export class StoreService {
	private previousRoute = '';
	pricingSignal = signal<Pricing | null>(null);
	private pricingPromise: Promise<Pricing> | null = null;
	private stripePromise: Promise<Stripe | null> | null = null;

	// Product selection state (stores the value string: 'yearly' or 'monthly')
	private selectedProductValueSignal = signal<string>('');

	// Computed signal that returns the full RadioButtonOption object
	private selectedProductOptionSignal = computed<RadioButtonOption | null>(() => {
		const selectedValue = this.selectedProductValueSignal();
		if (!selectedValue) {
			return null;
		}
		const options = this.getProductOptions();
		return options.find(option => option.value === selectedValue) || null;
	});

	/**
	 * Get product options available for selection
	 */
	getProductOptions(): RadioButtonOption[] {
		return [
			{
				title: '1 Year Subscription',
				subtitle: 'Full access to Scoutly Plans service for a year. Annually renewed.',
				label: 299.0,
				value: 'yearly'
			},
			{
				title: 'Monthly Subscription',
				subtitle: 'Full access to Scoutly Plans service for the month. Monthly renewed.',
				label: 29.0,
				value: 'monthly'
			}
		];
	}

	/**
	 * Get the selected product signal (returns RadioButtonOption object)
	 */
	getSelectedProductSignal() {
		return this.selectedProductOptionSignal;
	}

	/**
	 * Get the selected product value signal (returns string value: 'yearly' or 'monthly')
	 * Use this when you need the string value instead of the full option object
	 */
	getSelectedProductValueSignal() {
		return this.selectedProductValueSignal;
	}

	/**
	 * Set the selected product
	 */
	setSelectedProduct(product: string): void {
		this.selectedProductValueSignal.set(product);
		this.log.debug('Selected product set to:', product);
	}

	/**
	 * Clear the selected product
	 */
	clearSelectedProduct(): void {
		this.selectedProductValueSignal.set('');
		this.log.debug('Selected product cleared');
	}

	constructor(
		private apiService: ApiService,
		private planService: PlanService,
		private router: Router,
		private log: LogService,
		private ngxStripeService: StripeService,
		@Inject(PLATFORM_ID) private platformId: object
	) {}

	/**
	 * Initialize the service - loads pricing data
	 * Should be called from app component after browser check
	 */
	init(): void {
		// Only load pricing in browser environment
		if (isPlatformBrowser(this.platformId)) {
			void this.loadPricing();
		}
	}

	setPreviousRoute(route: string): void {
		this.previousRoute = route;
		this.log.debug('Store service: Previous route set to', route);
	}

	getPreviousRoute(): string {
		return this.previousRoute || '/';
	}

	/**
	 * Get pricing signal - pricing is loaded on service initialization
	 * Automatically initializes if not already done (browser only)
	 * @returns Signal containing pricing data or null if not yet loaded
	 */
	getPricingSignal() {
		// TODO: Move this to app startup instead of signal...
		// Lazy initialization - if pricing hasn't been loaded and we're in browser, load it now
		if (this.pricingSignal() === null && this.pricingPromise === null && isPlatformBrowser(this.platformId)) {
			this.init();
		}
		return this.pricingSignal;
	}

	/**
	 * Load pricing from API and update signal
	 * Only makes API call if pricing hasn't been loaded yet
	 */
	private loadPricing(): Promise<Pricing> {
		// Return existing promise if a fetch is in progress
		// This check must happen first to prevent race conditions
		if (this.pricingPromise !== null) {
			return this.pricingPromise;
		}

		// Create a promise resolver that we can control
		// This ensures atomic assignment to prevent race conditions
		let resolvePromise: (value: Pricing) => void;
		let _rejectPromise: (reason?: unknown) => void;

		// Create the promise synchronously and assign it immediately
		// This prevents multiple simultaneous calls from both passing the null check
		this.pricingPromise = new Promise<Pricing>((resolve, reject) => {
			resolvePromise = resolve;
			_rejectPromise = reject;
		});

		// Now start the async operation
		void (async () => {
			try {
				const response = await this.apiService.getStorePricing();
				this.log.debug('Pricing fetched:', response);
				this.pricingSignal.set(response);
				resolvePromise(response);
			} catch (error) {
				this.log.error('Error fetching pricing, using fallback:', error);
				// Fallback pricing
				const fallbackPricing: Pricing = {
					oneTime: 299,
					subscription: 29,
					currency: 'USD'
				};
				this.pricingSignal.set(fallbackPricing);
				resolvePromise(fallbackPricing);
			} finally {
				// Clear the promise so future calls can fetch again if cache is cleared
				this.pricingPromise = null;
			}
		})();

		return this.pricingPromise;
	}

	/**
	 * @deprecated Use getPricingSignal() instead. This method is kept for backward compatibility.
	 * Get pricing - fetches once and caches in signal
	 */
	async getPricing(): Promise<Pricing> {
		// Return cached pricing if available
		const cached = this.pricingSignal();
		if (cached) {
			return cached;
		}

		// Return existing promise if a fetch is in progress
		if (this.pricingPromise !== null) {
			return this.pricingPromise;
		}

		// Load pricing and return the promise
		return this.loadPricing();
	}

	clearPricingCache(): void {
		this.pricingSignal.set(null);
		this.pricingPromise = null;
		this.log.debug('Pricing cache cleared');
	}

	async initializeStripe(publishableKey: string): Promise<Stripe | null> {
		if (!isPlatformBrowser(this.platformId)) {
			return null;
		}

		// Return existing promise if already initialized or initializing.
		if (this.stripePromise !== null) {
			return this.stripePromise;
		}

		// Single source of truth: use ngx-stripe's loader/service to avoid duplicate Stripe.js injections.
		this.stripePromise = this.resolveStripeFromNgxStripe(publishableKey);
		return this.stripePromise;
	}

	private async resolveStripeFromNgxStripe(publishableKey: string): Promise<Stripe | null> {
		try {
			const existingInstance = this.ngxStripeService.getInstance() as Stripe | undefined;
			if (existingInstance) {
				return existingInstance;
			}

			// Triggers ngx-stripe loader and waits until Stripe reference is available.
			await firstValueFrom(this.ngxStripeService.getStripeReference());
			const loadedInstance = this.ngxStripeService.getInstance() as Stripe | undefined;
			if (loadedInstance) {
				return loadedInstance;
			}

			// Fallback to global Stripe factory once loader completes.
			const stripeOptions = {
				developerTools: {
					assistant: {
						enabled: environment.stripeDeveloperAssistantEnabled
					}
				}
			};
			const globalStripe = (globalThis as { Stripe?: (key: string, options?: unknown) => Stripe | null }).Stripe;
			return typeof globalStripe === 'function' ? globalStripe(publishableKey, stripeOptions) : null;
		} catch (error) {
			this.log.error('Error initializing Stripe via ngx-stripe loader:', error);
			return null;
		}
	}

	async createPaymentIntent(
		planId: string
	): Promise<{ clientSecret: string; paymentIntentId: string; accountId: string }> {
		try {
			const response = await this.apiService.createPaymentIntent(planId);
			this.log.debug('Payment intent created:', response);
			return response;
		} catch (error) {
			this.log.error('Error creating payment intent:', error);
			throw error;
		}
	}

	async createSubscription(
		planId: string,
		billingInterval?: 'month' | 'year'
	): Promise<{ clientSecret: string; subscriptionId: string; customerId: string; accountId: string }> {
		try {
			const response = await this.apiService.createSubscription(planId, billingInterval);
			this.log.debug('Subscription created:', response);
			return response;
		} catch (error) {
			this.log.error('Error creating subscription:', error);
			throw error;
		}
	}

	async verifyPayment(sessionId: string): Promise<PaymentVerification> {
		try {
			const plan = this.planService.selectedPlanSignal();
			if (!plan || !plan.id) {
				throw new Error('No plan selected');
			}

			const response = await this.apiService.verifyPayment(sessionId, plan.id);

			this.log.debug('Payment verified:', response);
			return response;
		} catch (error) {
			this.log.error('Error verifying payment:', error);
			throw error;
		}
	}

	async handlePaymentSuccess(sessionId: string): Promise<void> {
		try {
			const verification = await this.verifyPayment(sessionId);
			if (verification.success) {
				// Refresh plan data
				await this.planService.getFullPlanData(verification.plan.id);
				// Redirect to previous route
				const route = this.getPreviousRoute();
				this.log.debug('Redirecting to previous route:', route);
				await this.router.navigate([route]);
			} else {
				throw new Error('Payment verification failed');
			}
		} catch (error) {
			this.log.error('Error handling payment success:', error);
			// Redirect to dashboard on error
			await this.router.navigate(['/']);
		}
	}

	async openCustomerPortal(returnUrl?: string, openInNewTab = false): Promise<void> {
		let portalWindow: Window | null = null;
		try {
			const defaultReturnUrl = returnUrl || this.router.url || '/account';

			// Open a placeholder tab synchronously during the user gesture to avoid popup blockers.
			if (openInNewTab) {
				portalWindow = window.open('', '_blank');
			}

			const response = await this.apiService.createPortalSession(defaultReturnUrl);
			this.log.debug('Portal session created, redirecting:', { ...response, openInNewTab });
			if (openInNewTab) {
				if (portalWindow && !portalWindow.closed) {
					portalWindow.location.href = response.url;
					return;
				}

				const popup = window.open(response.url, '_blank');
				if (!popup) {
					this.log.warn('Unable to open customer portal in a new tab. Redirecting in current tab.');
					window.location.href = response.url;
					return;
				}

				return;
			}
			window.location.href = response.url;
		} catch (error) {
			if (portalWindow && !portalWindow.closed) {
				portalWindow.close();
			}
			this.log.error('Error opening customer portal:', error);
			throw error;
		}
	}
}
